import React, { useState, useEffect } from "react";
import {
  Grid,
  Divider,
  Box,
  IconButton,
  Popover,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import API_BASE_URL from 'config/api';
import {
  Circle,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { ChromePicker } from "react-color";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import {
  ConfigCard,
  BlueButton,
} from "styles";
import BannerPreview from "components/BannerPreview";
import LivePreview from "components/LivePreview";

const popularColors = [
  "#FFFFFF",
  "#000000",
  "#1976D2",
  "#D32F2F",
  "#388E3C",
  "#FFA000",
  "#F5F5F5",
  "#212121",
  "#2196F3",
  "#F44336",
  "#4CAF50",
  "#FFC107",
];

const BannerDesign = () => {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [bannerSettings, setBannerSettings] = useState(null);
  const [userMeData, setUserMeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewTabValue, setPreviewTabValue] = useState(0);
  const [activeLang, setActiveLang] = useState("tr");
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functionality: false,
    analytics: false,
    marketing: false,
  });
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentDomainSettings, setCurrentDomainSettings] = useState(null);
  const [currentLanguageSettings, setCurrentLanguageSettings] = useState(null);
  const [cookies, setCookies] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentColorKey, setCurrentColorKey] = useState("");
  const [pickerColor, setPickerColor] = useState("#ffffff");

  useEffect(() => {
    const loadUserData = async () => {
      const cachedUserData = sessionStorage.getItem("userMeData");
      if (cachedUserData) {
        setUserMeData(JSON.parse(cachedUserData));
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/me`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        });
        const data = await response.json();
        sessionStorage.setItem("userMeData", JSON.stringify(data));
        setUserMeData(data);
      } catch (error) {
        console.error("Error loading user data:", error);
        showSnackbar("Kullanıcı verileri yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  useEffect(() => {
    const loadBannerSettings = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        const cachedSettings = sessionStorage.getItem("bannerSettings");

        if (cachedSettings) {
          const parsedSettings = JSON.parse(cachedSettings);
          setBannerSettings(parsedSettings);
          setDomains(Object.keys(parsedSettings));

          if (Object.keys(parsedSettings).length > 0) {
            const firstDomain = Object.keys(parsedSettings)[0];
            setSelectedDomain(firstDomain);
            setCurrentDomainSettings(parsedSettings[firstDomain]);
            if (parsedSettings[firstDomain].bannerSettings[activeLang]) {
              setCurrentLanguageSettings(
                parsedSettings[firstDomain].bannerSettings[activeLang]
              );
            } else {
              const availableLangs = Object.keys(
                parsedSettings[firstDomain].bannerSettings
              );
              if (availableLangs.length > 0) {
                setActiveLang(availableLangs[0]);
                setCurrentLanguageSettings(
                  parsedSettings[firstDomain].bannerSettings[availableLangs[0]]
                );
              }
            }
          }
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/banner-settings/by-tenant?tenantId=${userMeData.user.clients[0].clientId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
          }
        );
        const data = await response.json();
        sessionStorage.setItem("bannerSettings", JSON.stringify(data));
        setBannerSettings(data);
        setDomains(Object.keys(data));

        if (Object.keys(data).length > 0) {
          const firstDomain = Object.keys(data)[0];
          setSelectedDomain(firstDomain);
          setCurrentDomainSettings(data[firstDomain]);
          if (data[firstDomain].bannerSettings[activeLang]) {
            setCurrentLanguageSettings(
              data[firstDomain].bannerSettings[activeLang]
            );
          } else {
            const availableLangs = Object.keys(
              data[firstDomain].bannerSettings
            );
            if (availableLangs.length > 0) {
              setActiveLang(availableLangs[0]);
              setCurrentLanguageSettings(
                data[firstDomain].bannerSettings[availableLangs[0]]
              );
            }
          }
        }
      } catch (error) {
        console.error("Error loading banner settings:", error);
        showSnackbar("Banner ayarları yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadBannerSettings();
  }, [userMeData]);

  useEffect(() => {
    const loadCookieData = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        const cachedSettings = sessionStorage.getItem("userCookies");

        if (cachedSettings && Object.keys(cachedSettings).length > 0) {
          const parsedSettings = JSON.parse(cachedSettings);
          setCookies(parsedSettings);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/cookie-definition/all?tenantId=${userMeData.user.clients[0].clientId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
          }
        );
        const data = await response.json();

        if (Object.keys(data).length > 0) {
          sessionStorage.setItem("userCookies", JSON.stringify(data));
          setCookies(data);
        }
      } catch (error) {
        console.error("Error loading cookies:", error);
        showSnackbar("Çerezler yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadCookieData();
  }, [userMeData]);

  useEffect(() => {
    if (selectedDomain && bannerSettings && bannerSettings[selectedDomain]) {
      const domainSettings = bannerSettings[selectedDomain];
      setCurrentDomainSettings(domainSettings);

      const defaultLang = domainSettings.defaultLanguage || activeLang;

      if (domainSettings.bannerSettings[defaultLang]) {
        setActiveLang(defaultLang);
        setCurrentLanguageSettings(domainSettings.bannerSettings[defaultLang]);
      } else if (domainSettings.bannerSettings[activeLang]) {
        setCurrentLanguageSettings(domainSettings.bannerSettings[activeLang]);
      } else {
        const availableLangs = Object.keys(domainSettings.bannerSettings);
        if (availableLangs.length > 0) {
          const fallbackLang = availableLangs[0];
          setActiveLang(fallbackLang);
          setCurrentLanguageSettings(
            domainSettings.bannerSettings[fallbackLang]
          );
        }
      }
    }
  }, [selectedDomain]);

  useEffect(() => {
    if (!selectedDomain || !bannerSettings) return;
    const domainSettings = bannerSettings[selectedDomain];
    if (!domainSettings) return;
    const candidate = domainSettings.bannerSettings?.[activeLang];
    if (candidate) {
      setCurrentLanguageSettings(candidate);
    } else {
      const fallback = Object.keys(domainSettings.bannerSettings || {})[0];
      if (fallback) {
        setActiveLang(fallback);
        setCurrentLanguageSettings(domainSettings.bannerSettings[fallback]);
      }
    }
  }, [activeLang, selectedDomain, bannerSettings]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCategoryToggle = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDomainChange = (event) => {
    setSelectedDomain(event.target.value);
  };

  const handlePreviewTabChange = (event, newValue) => {
    setPreviewTabValue(newValue);
  };

  const handlePopoverOpen = (event, key) => {
    setCurrentColorKey(key);
    setPickerColor(
      bannerSettings[selectedDomain].themeSettings.themeColors[key]
    );
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handlePickerChange = (color) => {
    setPickerColor(color.hex);
  };

  const handlePickerChangeComplete = (color) => {
    const newColor = color.hex;
    const newSettings = { ...bannerSettings };
    newSettings[selectedDomain].themeSettings.themeColors[currentColorKey] =
      newColor;
    setBannerSettings(newSettings);
  };

  const handlePopularColorSelect = (color) => {
    setPickerColor(color);
    const newSettings = { ...bannerSettings };
    newSettings[selectedDomain].themeSettings.themeColors[currentColorKey] =
      color;
    setBannerSettings(newSettings);
    handlePopoverClose();
  };

  const handleColorInputChange = (event, key) => {
    const newValue = event.target.value;
    const newSettings = { ...bannerSettings };
    newSettings[selectedDomain].themeSettings.themeColors[key] = newValue;
    setBannerSettings(newSettings);
  };

  const handleLangChange = (e) => {
    setActiveLang(e.target.value);
  };

  const handlePreferenceChange = (category) => (event) => {
    setCookiePreferences({
      ...cookiePreferences,
      [category]: event.target.checked,
    });
  };

  const handleAcceptAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleCustomize = () => {
    setOpenModal(!openModal);
  };

  const handleAcceptSelection = () => {
    setOpenModal(false);
  };

  const handleSave = async () => {
    if (!bannerSettings || !selectedDomain || !userMeData) return;

    try {
      setSaving(true);
      const currentSettings = bannerSettings[selectedDomain];
      const { themeSettings, id } = currentSettings;

      const payload = {
        siteId: id,
        themeColors: themeSettings.themeColors,
      };

      const response = await fetch(
        `${API_BASE_URL}/theme-settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Güncelleme başarısız oldu");
      }

      const updatedSettings = { ...bannerSettings };
      updatedSettings[selectedDomain].themeSettings.themeColors = {
        ...updatedSettings[selectedDomain].themeSettings.themeColors,
      };

      sessionStorage.setItem("bannerSettings", JSON.stringify(updatedSettings));
      setBannerSettings(updatedSettings);

      showSnackbar("Renk ayarları başarıyla kaydedildi", "success");
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      showSnackbar("Renk ayarları kaydedilirken hata oluştu", "error");
    } finally {
      setSaving(false);
    }
  };

  if (
    selectedDomain &&
    bannerSettings &&
    bannerSettings[selectedDomain] &&
    Object.keys(bannerSettings[selectedDomain].bannerSettings || {}).length === 0
  ) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
          flexDirection="column"
        >
          <Typography variant="h6" mb={2}>
            Bir Sitenizde dil ayarlarını tamamlamadınız.
          </Typography>
          <BlueButton onClick={() => window.location.href = "/configuration/language"}>
            Dil Ayarlarına Git
          </BlueButton>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (
    !bannerSettings ||
    !selectedDomain ||
    !currentDomainSettings ||
    !currentLanguageSettings
  ) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress size={50} />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const themeSettings = currentDomainSettings.themeSettings;
  const themeColors = themeSettings.themeColors;
  const availableLangs = Object.keys(currentDomainSettings.bannerSettings);

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ConfigCard>
              <MDBox p={3}>
                <MDTypography
                  variant="h5"
                  fontWeight="medium"
                  mb={3}
                  color="info"
                >
                  Banner Tasarımı
                </MDTypography>

                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  Domain
                </MDTypography>
                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                      background: "#fafbfc",
                      fontSize: 16,
                      padding: "4px 12px",
                      "& .MuiSelect-icon": {
                        color: "#000",
                        right: "12px",
                      },
                    },
                  }}
                >
                  <Select
                    value={selectedDomain}
                    onChange={handleDomainChange}
                    IconComponent={(props) => (
                      <KeyboardArrowDown {...props} fontSize="medium" />
                    )}
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                      borderRadius: "12px",
                      ".MuiSelect-select": {
                        padding: "14px 32px 14px 12px",
                      },
                      "& .MuiSelect-icon": {
                        display: "block !important",
                      },
                    }}
                  >
                    {domains.map((domain) => (
                      <MenuItem key={domain} value={domain}>
                        {domain}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {[
                  { key: "backgroundColor", label: "Banner Arka Plan Rengi" },
                  { key: "textColor", label: "Banner Yazı Rengi" },
                  { key: "buttonBgColor", label: "Buton Arka Plan Rengi" },
                  { key: "buttonTextColor", label: "Buton Yazı Rengi" },
                ].map((item) => (
                  <React.Fragment key={item.key}>
                    <MDTypography variant="body2" fontWeight="medium" mb={1}>
                      {item.label}
                    </MDTypography>

                    <MDBox display="flex" alignItems="center" gap={2} mb={3}>
                      <MDBox
                        component="input"
                        type="text"
                        value={themeColors[item.key]}
                        onChange={(e) => handleColorInputChange(e, item.key)}
                        sx={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "1px solid #ced4da",
                          borderRadius: "6px",
                          fontSize: "0.875rem",
                          "&:focus": {
                            outline: "none",
                            borderColor: "#3f51b5",
                          },
                        }}
                      />

                      <IconButton
                        onClick={(e) => handlePopoverOpen(e, item.key)}
                        sx={{
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          p: 1,
                        }}
                      >
                        <Circle
                          sx={{
                            color: themeColors[item.key],
                            fontSize: 28,
                            border:
                              themeColors[item.key] === "#FFFFFF"
                                ? "1px solid #ddd"
                                : "none",
                          }}
                        />
                      </IconButton>
                    </MDBox>
                  </React.Fragment>
                ))}

                <Divider sx={{ my: 3 }} />
                <MDBox mt={2}>
                  <BlueButton
                    fullWidth
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </BlueButton>
                </MDBox>
              </MDBox>
            </ConfigCard>
          </Grid>

          <Grid item xs={12} md={8}>
            <LivePreview
              cookieContentState={{
                banner: <BannerPreview
                  themeSettings={themeSettings}
                  currentLanguageSettings={currentLanguageSettings}
                  currentDomainSettings={currentDomainSettings}
                  cookies={cookies}
                  activeLang={activeLang}
                  previewTabValue={previewTabValue}
                  openModal={openModal}
                  cookiePreferences={cookiePreferences}
                  expandedCategories={expandedCategories}
                  availableLangs={availableLangs}
                  onLangChange={handleLangChange}
                  onPreviewTabChange={handlePreviewTabChange}
                  onCustomize={handleCustomize}
                  onAcceptAll={handleAcceptAll}
                  onRejectAll={handleRejectAll}
                  onAcceptSelection={handleAcceptSelection}
                  onPreferenceChange={handlePreferenceChange}
                  onCategoryToggle={handleCategoryToggle}
                  onSnackbar={showSnackbar}
                />
              }}
              selectedDomain={selectedDomain}
              defaultLanguage={currentDomainSettings.defaultLanguage}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPopover-paper": {
            overflow: "visible",
            padding: 1,
            borderRadius: "8px",
            background: "white",
          },
        }}
      >
        <MDBox
          sx={{ width: 330, height: 250, background: "white" }}
          display="flex"
          alignItems="center"
          p={2}
          gap={2}
        >
          <ChromePicker
            color={pickerColor}
            onChange={handlePickerChange}
            onChangeComplete={handlePickerChangeComplete}
            disableAlpha
          />
          <MDBox
            display="flex"
            flexDirection="column"
            gap={1}
            flexWrap="wrap"
            mb={2}
            mt={2}
            sx={{ maxHeight: 200, overflowY: "auto" }}
          >
            {popularColors.map((color) => (
              <IconButton
                key={color}
                onClick={() => handlePopularColorSelect(color)}
                sx={{ p: 0 }}
              >
                <Circle
                  sx={{
                    color,
                    fontSize: 32,
                    border: color === "#FFFFFF" ? "1px solid #ddd" : "none",
                  }}
                />
              </IconButton>
            ))}
          </MDBox>
        </MDBox>
      </Popover>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BannerDesign;